import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import StatsStrip from './components/StatsStrip';
import ProcessSteps from './components/ProcessSteps';
import ServiceTestimonials from './components/ServiceTestimonials';
import useInView from '../../hooks/useInView';
import type { ServicePageData } from '../../mocks/servicePages';

interface ServicePageTemplateProps {
  data: ServicePageData;
}

const FaqItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
      >
        <span className="text-sm font-semibold text-[#111827]">{q}</span>
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-4">
          <i className={`ri-arrow-down-s-line text-[#9CA3AF] transition-transform text-lg ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {open && (
        <div className="px-6 pb-5 text-sm text-[#6B7280] leading-relaxed border-t border-[#E2E8F0] pt-4">
          {a}
        </div>
      )}
    </div>
  );
};

const ServicePageTemplate = ({ data }: ServicePageTemplateProps) => {
  const navigate = useNavigate();
  const heroAnim = useInView({ threshold: 0.05 });
  const benefitsAnim = useInView({ threshold: 0.1 });
  const detailsAnim = useInView({ threshold: 0.1 });
  const faqAnim = useInView({ threshold: 0.1 });
  const ctaAnim = useInView({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="relative nav-offset bg-[#0C2340]">
        <div className="absolute inset-0">
          <img
            alt={data.heroImgAlt}
            className="w-full h-full object-cover object-center opacity-25"
            src={data.heroImg}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0C2340] via-[#0C2340]/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-28" ref={heroAnim.ref}>
          <div className="max-w-2xl">
            <p
              className="text-[#C41E3A] text-sm font-bold uppercase tracking-widest mb-3 transition-all duration-700"
              style={{ opacity: heroAnim.inView ? 1 : 0, transform: heroAnim.inView ? 'translateY(0)' : 'translateY(16px)' }}
            >
              {data.title}
            </p>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight transition-all duration-700 delay-100"
              style={{ opacity: heroAnim.inView ? 1 : 0, transform: heroAnim.inView ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '100ms' }}
            >
              {data.headline}
            </h1>
            <p
              className="text-base sm:text-xl text-white/80 mb-7 sm:mb-10 transition-all duration-700"
              style={{ opacity: heroAnim.inView ? 1 : 0, transform: heroAnim.inView ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '200ms' }}
            >
              {data.subheadline}
            </p>
            <div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 transition-all duration-700"
              style={{ opacity: heroAnim.inView ? 1 : 0, transform: heroAnim.inView ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '300ms' }}
            >
              <button
                onClick={() => navigate('/book-appointment')}
                className="bg-[#C41E3A] text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-[#A01830] transition-colors whitespace-nowrap cursor-pointer"
              >
                Book Appointment
              </button>
              <a
                href="tel:+18506698335"
                className="border border-white/30 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-white/10 transition-colors whitespace-nowrap flex items-center justify-center space-x-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-sm" />
                </div>
                <span>(850) 669-8335</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Benefits Bar ─────────────────────────────────────────── */}
      <section className="py-10 md:py-14 bg-white border-b border-[#E2E8F0]" ref={benefitsAnim.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.benefits.map((b, i) => (
              <div
                key={b.title}
                className="flex items-start space-x-4 transition-all duration-600"
                style={{
                  opacity: benefitsAnim.inView ? 1 : 0,
                  transform: benefitsAnim.inView ? 'translateY(0)' : 'translateY(20px)',
                  transitionDelay: `${i * 80}ms`,
                }}
              >
                <div className="w-11 h-11 bg-[#C41E3A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${b.icon} text-xl text-[#C41E3A]`} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-[#111827] mb-1">{b.title}</h3>
                  <p className="text-xs text-[#6B7280] leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats Strip ──────────────────────────────────────────── */}
      <StatsStrip />

      {/* ── About + Services ─────────────────────────────────────── */}
      <section className="py-14 sm:py-20 bg-white" ref={detailsAnim.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div
              className="transition-all duration-700"
              style={{ opacity: detailsAnim.inView ? 1 : 0, transform: detailsAnim.inView ? 'translateX(0)' : 'translateX(-24px)' }}
            >
              <p className="text-[#C41E3A] text-xs font-bold uppercase tracking-widest mb-2">About This Service</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#111827] mb-5">
                Expert {data.title} You Can Trust
              </h2>
              <p className="text-base sm:text-lg text-[#6B7280] mb-7 leading-relaxed">{data.description}</p>
              <div className="bg-[#F8F9FB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-sm font-bold text-[#111827] mb-4 flex items-center space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shield-check-fill text-[#C41E3A]" />
                  </div>
                  <span>The Renova Promise</span>
                </h3>
                <ul className="space-y-2.5">
                  {[
                    'Licensed & insured in all markets',
                    'Financing available from $0 down',
                    'NATE-certified technicians',
                    '10-year parts & labor warranty',
                    '24/7 emergency service',
                    '100% satisfaction guarantee',
                  ].map((item) => (
                    <li key={item} className="flex items-center space-x-2.5 text-sm text-[#374151]">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-[#C41E3A] text-base" />
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div
              className="transition-all duration-700"
              style={{ opacity: detailsAnim.inView ? 1 : 0, transform: detailsAnim.inView ? 'translateX(0)' : 'translateX(24px)', transitionDelay: '120ms' }}
            >
              <p className="text-[#C41E3A] text-xs font-bold uppercase tracking-widest mb-2">What We Offer</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#111827] mb-6">
                Our {data.title}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.services.map((s) => (
                  <div
                    key={s.title}
                    className="p-5 bg-[#F8F9FB] rounded-xl border border-[#E2E8F0] hover:border-[#C41E3A]/40 hover:bg-white transition-all cursor-default"
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-5 h-5 bg-[#C41E3A]/10 rounded flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-[#C41E3A] text-xs" />
                      </div>
                      <h3 className="text-sm font-bold text-[#111827]">{s.title}</h3>
                    </div>
                    <p className="text-xs text-[#6B7280] leading-relaxed pl-7">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Process Steps ────────────────────────────────────────── */}
      <ProcessSteps steps={data.process} />

      {/* ── Testimonials ─────────────────────────────────────────── */}
      <ServiceTestimonials testimonials={data.testimonials} serviceTitle={data.title} />

      {/* ── FAQ ──────────────────────────────────────────────────── */}
      <section className="py-14 sm:py-20 bg-white" ref={faqAnim.ref}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center mb-10 transition-all duration-700"
            style={{ opacity: faqAnim.inView ? 1 : 0, transform: faqAnim.inView ? 'translateY(0)' : 'translateY(20px)' }}
          >
            <p className="text-[#C41E3A] text-xs font-bold uppercase tracking-widest mb-2">FAQ</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#111827]">Common Questions</h2>
            <p className="text-[#6B7280] mt-2 text-sm sm:text-base">Everything you need to know before booking.</p>
          </div>
          <div className="space-y-3">
            {data.faqs.map((faq, i) => (
              <div
                key={faq.q}
                className="transition-all duration-700"
                style={{
                  opacity: faqAnim.inView ? 1 : 0,
                  transform: faqAnim.inView ? 'translateY(0)' : 'translateY(20px)',
                  transitionDelay: `${i * 80}ms`,
                }}
              >
                <FaqItem q={faq.q} a={faq.a} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────── */}
      <section className="relative bg-[#0C2340] overflow-hidden" ref={ctaAnim.ref}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C41E3A] rounded-full translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#C41E3A] rounded-full -translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: Main CTA content */}
            <div
              className="transition-all duration-700"
              style={{ opacity: ctaAnim.inView ? 1 : 0, transform: ctaAnim.inView ? 'translateX(0)' : 'translateX(-24px)' }}
            >
              <p className="text-[#C41E3A] text-xs font-bold uppercase tracking-widest mb-3">Get Started Today</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                Ready to Schedule Your {data.title}?
              </h2>
              <p className="text-white/70 mb-2 text-base">
                Fast, professional service from NATE-certified technicians.
              </p>
              <p className="text-white/50 text-sm mb-8 flex items-center space-x-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-[#C41E3A]" />
                </div>
                <span>Most jobs scheduled within 24 hours</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/book-appointment')}
                  className="bg-[#C41E3A] text-white px-8 py-4 rounded-lg text-base font-bold hover:bg-[#A01830] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Book Appointment
                </button>
                <a
                  href="tel:+18506698335"
                  className="border-2 border-white/30 text-white px-8 py-4 rounded-lg text-base font-bold hover:border-white/60 transition-all flex items-center justify-center space-x-2 whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-fill" />
                  </div>
                  <span>(850) 669-8335</span>
                </a>
              </div>
            </div>

            {/* Right: Trust cards */}
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 transition-all duration-700"
              style={{ opacity: ctaAnim.inView ? 1 : 0, transform: ctaAnim.inView ? 'translateX(0)' : 'translateX(24px)', transitionDelay: '150ms' }}
            >
              {[
                {
                  icon: 'ri-customer-service-2-line',
                  title: '24/7 Emergency Service',
                  desc: 'Our technicians are on call around the clock for urgent situations.',
                },
                {
                  icon: 'ri-bank-card-line',
                  title: 'Financing from $0 Down',
                  desc: 'Flexible monthly payments available on qualifying projects.',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '10-Year Warranty',
                  desc: 'Parts and labor warranty included on qualifying system installs.',
                },
                {
                  icon: 'ri-star-line',
                  title: '4.9★ Customer Rating',
                  desc: 'Thousands of 5-star reviews across the Gulf Coast area.',
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors"
                >
                  <div className="w-9 h-9 flex items-center justify-center mb-3">
                    <i className={`${card.icon} text-2xl text-[#C41E3A]`} />
                  </div>
                  <h3 className="text-sm font-bold text-white mb-1">{card.title}</h3>
                  <p className="text-xs text-white/50 leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePageTemplate;
